import { ProjectRole } from '@/features/projects/shared/types/project-role';
import { Database, supabase } from '@/lib/supabase';
import { Row } from '@/shared/types/api';
import { formatDate } from '@/shared/utils/format';

type Partner = Row<'partner_aliases'>;

/** Public-safe subset of org fields returned by get_partners / get_filtered_org_partners */
export type OrgPublic = Database['public']['CompositeTypes']['org_public'];

export type PartnerPopulated = Partner & {
  organization: OrgPublic;
  active_projects: number;
  partner_status: string;
  partner_role: ProjectRole | null;
};

export const getPartners = async (id: number): Promise<PartnerPopulated[]> => {
  const { data, error } = await supabase.rpc('get_partners', {
    p_owner_orga_id: id,
  });

  if (error) throw error;

  const partners: PartnerPopulated[] = data.map((item) => ({
    ...item.partner_alias,
    organization: item.orga,
    active_projects: item.active_projects,
    partner_status: item.partner_status,
    partner_role: null,
  }));

  return partners;
};

export interface PartnerFilters {
  roles: ProjectRole[] | undefined;
  partnerIds: string[] | undefined;
  searchQuery: string | undefined;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
}

export const getFilteredPartners = async ({
  orgId,
  filters,
}: {
  orgId: number;
  filters: PartnerFilters;
}): Promise<PartnerPopulated[]> => {
  const { data, error } = await supabase.rpc('get_filtered_org_partners', {
    p_owner_orga_id: orgId,
    p_roles: filters?.roles,
    p_partner_ids: filters?.partnerIds,
    p_search_query: filters?.searchQuery,
    p_date_from: filters?.dateFrom
      ? formatDate({ date: filters.dateFrom, formatType: 'iso' })
      : undefined,
    p_date_to: filters?.dateTo
      ? formatDate({ date: filters.dateTo, formatType: 'iso' })
      : undefined,
  });

  if (error) throw error;

  const partners: PartnerPopulated[] = (data ?? []).map((item: any) => ({
    ...item.partner_alias,
    organization: item.orga,
    active_projects: item.active_projects,
    partner_status: item.partner_status,
    partner_role: item.partner_role,
  }));
  return partners;
};
